/**
 * Variante `stitch`: sidebar chiara e barra superiore in vetro.
 *
 * Ricalca il mockup, con una differenza dichiarata: le voci `Shared`, `Starred`
 * e `Trash` non ci sono. Non è una semplificazione grafica — quelle funzioni il
 * portale non le ha, e una voce di menu che non porta da nessuna parte è un
 * bug di prodotto travestito da decorazione. Al loro posto le sezioni vere.
 *
 * La CTA della sidebar è «Carica documenti» invece di «New Folder»: creare
 * cartelle è un permesso che i clienti quasi mai hanno, caricare è l'azione per
 * cui esiste il portale.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useSession } from '../lib/session.jsx';
import { ThemeToggle } from './ThemeToggle.jsx';
import '../styles/stitch.css';

type Section = { to: string; label: string; icon: string; adminOnly?: boolean };

const SECTIONS: Section[] = [
  { to: '/documenti', label: 'Documenti', icon: 'folder' },
  { to: '/organizzazioni', label: 'Organizzazioni', icon: 'apartment', adminOnly: true },
  { to: '/template', label: 'Template', icon: 'rule', adminOnly: true },
  { to: '/utenti', label: 'Utenti', icon: 'group', adminOnly: true },
  { to: '/log', label: 'Log', icon: 'receipt_long', adminOnly: true },
];

export function StitchShell() {
  const { me, logout } = useSession();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [navOpen, setNavOpen] = useState(false);

  const switchClient = useMutation({
    mutationFn: (clientId: number) => api.switchClient(clientId),
    onSuccess: () => queryClient.invalidateQueries(),
  });

  const sections = SECTIONS.filter((s) => !s.adminOnly || me.user.isAdmin);
  const canSwitch = me.clients.length > 1 || me.user.isInternal;
  const onDocuments = location.pathname.startsWith('/documenti');

  return (
    <div className="st">
      <aside className={`st__side${navOpen ? ' is-open' : ''}`}>
        <div className="st__side-head">
          {/* Il marchio sta qui, in cima alla navigazione; la barra superiore
              tiene solo il nome del prodotto. */}
          <img className="st__side-logo" src="/methodica-logo.png" alt="Methodica" />
          <p className="st__side-sub">{me.activeClient?.displayName ?? 'Nessuna organizzazione'}</p>
        </div>

        <nav className="st__nav" aria-label="Sezioni">
          {sections.map((s) => (
            <NavLink
              key={s.to}
              to={s.to}
              onClick={() => setNavOpen(false)}
              className={({ isActive }) => (isActive ? 'is-active' : undefined)}
            >
              <Icon name={s.icon} filled />
              <span>{s.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="st__side-foot">
          <ThemeToggle />

          <div className="st__user">
            <span className="st__user-text">
              <strong>{me.user.displayName ?? me.user.username}</strong>
              <span>{me.user.isAdmin ? 'amministratore' : me.user.username}</span>
            </span>
            <button type="button" className="st__icon-btn st__logout" onClick={logout} title="Esci">
              <Icon name="logout" />
              <span className="visually-hidden">Esci</span>
            </button>
          </div>
        </div>
      </aside>

      {navOpen ? (
        <button
          type="button"
          className="st__scrim"
          aria-label="Chiudi menu"
          onClick={() => setNavOpen(false)}
        />
      ) : null}

      <div className="st__main">
        <header className="st__topbar">
          <div className="st__brand">
            <button
              type="button"
              className="st__burger"
              aria-label="Menu"
              aria-expanded={navOpen}
              onClick={() => setNavOpen((o) => !o)}
            >
              <Icon name="menu" />
            </button>
            {/*
              Due parti e non una: «my» va piu' marcato di «methodica». Restano
              `span` e non `strong`, perche' l'enfasi qui e' del marchio, non del
              discorso — a chi legge con uno screen reader la frase arriva
              identica, e `strong` porterebbe con se' un grassetto da disfare.
            */}
            <span className="st__brand-name">
              <span className="st__brand-my">my</span>{' '}
              <span className="st__brand-nome">methodica</span>
            </span>
          </div>

          {canSwitch && onDocuments ? (
            <label className="st__org">
              <Icon name="apartment" />
              <span className="visually-hidden">Organizzazione</span>
              <select
                className="st__org-select"
                value={me.activeClient?.id ?? ''}
                disabled={switchClient.isPending}
                onChange={(e) => switchClient.mutate(Number(e.target.value))}
              >
                <option value="" disabled>
                  Seleziona…
                </option>
                {me.clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.displayName}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </header>

        <Outlet />
      </div>
    </div>
  );
}

function Icon({ name, filled }: { name: string; filled?: boolean }) {
  return (
    <span className={`material-symbols-outlined${filled ? ' is-filled' : ''}`} aria-hidden="true">
      {name}
    </span>
  );
}
